/**
 * API Transform Utilities (FR-080)
 *
 * Provides snake_case ↔ camelCase transformation for API payloads.
 * Applied at the hook level to normalize backend responses.
 */

/**
 * Convert a snake_case string to camelCase.
 * @param {string} str
 * @returns {string}
 */
export function snakeToCamelStr(str) {
  return str.replace(/_([a-z0-9])/g, (_, char) => char.toUpperCase());
}

/**
 * Convert a camelCase string to snake_case.
 * @param {string} str
 * @returns {string}
 */
export function camelToSnakeStr(str) {
  return str.replace(/[A-Z]/g, (char) => `_${char.toLowerCase()}`);
}

/**
 * Recursively transform all keys of an object from snake_case to camelCase.
 * Handles nested objects and arrays. Primitives are returned as-is.
 *
 * @param {*} data - The data to transform
 * @returns {*} - Transformed data with camelCase keys
 */
export function snakeToCamel(data) {
  if (data === null || data === undefined || typeof data !== 'object') {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map(snakeToCamel);
  }

  if (data instanceof Date) {
    return data;
  }

  const result = {};
  for (const [key, value] of Object.entries(data)) {
    const camelKey = snakeToCamelStr(key);
    result[camelKey] = snakeToCamel(value);
  }
  return result;
}

/**
 * Recursively transform all keys of an object from camelCase to snake_case.
 * Handles nested objects and arrays. Primitives are returned as-is.
 *
 * @param {*} data - The data to transform
 * @returns {*} - Transformed data with snake_case keys
 */
export function camelToSnake(data) {
  if (data === null || data === undefined || typeof data !== 'object') {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map(camelToSnake);
  }

  if (data instanceof Date) {
    return data;
  }

  const result = {};
  for (const [key, value] of Object.entries(data)) {
    const snakeKey = camelToSnakeStr(key);
    result[snakeKey] = camelToSnake(value);
  }
  return result;
}
